import { useQuery } from "@tanstack/react-query";
import Layout from "@/components/Layout";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api } from "@/lib/api";

export default function ParentDashboard() {
  const { data, isLoading } = useQuery({
    queryKey: ["parent-dashboard"],
    queryFn: async () => (await api.get("/parent/dashboard")).data,
  });

  return (
    <Layout title="Child's Attendance">
      {isLoading || !data ? (
        <p className="text-sm text-slate-500" data-testid="parent-loading">Loading…</p>
      ) : (
        <div className="space-y-6" data-testid="parent-dashboard">
          <div className="bg-white border border-slate-200 rounded-md p-5">
            <p className="text-xs text-slate-400 uppercase tracking-wider">Student</p>
            <p className="text-lg font-semibold text-slate-950" data-testid="parent-student-name">{data.student_name}</p>
            <p className="text-sm text-slate-500">
              <span className="font-data">{data.roll_number}</span> · {data.department_name} · {data.semester_name}
            </p>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="parent-overall-card">
              <p className="text-xs text-slate-400 uppercase tracking-wider">Overall attendance</p>
              <p className={`text-2xl font-data font-semibold ${data.overall_percentage != null && data.overall_percentage < data.threshold ? "text-red-600" : "text-slate-950"}`}
                data-testid="parent-overall-percentage">
                {data.overall_percentage != null ? `${data.overall_percentage}%` : "—"}
              </p>
              <p className="text-xs text-slate-400 mt-1">Minimum required: {data.threshold}%</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="parent-present-card">
              <p className="text-xs text-slate-400 uppercase tracking-wider">Classes attended</p>
              <p className="text-2xl font-data font-semibold text-slate-950">{data.total_present}</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="parent-total-card">
              <p className="text-xs text-slate-400 uppercase tracking-wider">Total classes</p>
              <p className="text-2xl font-data font-semibold text-slate-950">{data.total_classes}</p>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="parent-subjects-card">
            <p className="text-sm font-semibold text-slate-950 mb-3">Subject-wise attendance</p>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Subject</TableHead>
                  <TableHead className="text-right">Attended</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">%</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.per_subject.map((s) => (
                  <TableRow key={s.subject_id} data-testid={`parent-subject-${s.subject_id}`}>
                    <TableCell>{s.name} <span className="text-xs text-slate-400 font-data">{s.code}</span></TableCell>
                    <TableCell className="text-right font-data">{s.present}</TableCell>
                    <TableCell className="text-right font-data">{s.total}</TableCell>
                    <TableCell className={`text-right font-data ${s.percentage != null && s.percentage < data.threshold ? "text-red-600" : ""}`}>
                      {s.percentage != null ? `${s.percentage}%` : "—"}
                    </TableCell>
                  </TableRow>
                ))}
                {data.per_subject.length === 0 && (
                  <TableRow><TableCell colSpan={4} className="text-center text-slate-400 py-6">No subjects yet</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="parent-history-card">
            <p className="text-sm font-semibold text-slate-950 mb-3">Recent classes</p>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead>Topic</TableHead>
                  <TableHead className="text-right">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.history.slice(0, 15).map((h) => (
                  <TableRow key={h.id} data-testid={`parent-history-${h.id}`}>
                    <TableCell className="font-data">{h.date}</TableCell>
                    <TableCell>{h.period_label}</TableCell>
                    <TableCell>{h.subject_name}</TableCell>
                    <TableCell className="max-w-56 truncate">{h.topic || "—"}</TableCell>
                    <TableCell className="text-right">
                      <Badge variant="outline" className={h.status === "present"
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : "bg-red-50 text-red-700 border-red-200"}>
                        {h.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
                {data.history.length === 0 && (
                  <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-6">No classes recorded yet</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </Layout>
  );
}
